/**
 * GivingIdentityRequestsPage — asking to see a giver's name, and deciding.
 *
 * Above assembly level, giving is reported against a stable coded reference
 * (`Giver 7F3A`) rather than a name. Seeing the person behind the reference is
 * not a setting anyone can switch: it is requested with a reason, approved by
 * somebody else, scoped to named units and a period, and it expires.
 *
 * The screen is deliberately two columns, not one list: what *I* asked for,
 * and what is waiting for *my* decision. Nobody can approve their own request
 * — the server refuses it (403) and this screen does not offer the button.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, EyeOff, ShieldCheck, Clock, Ban, Check, X, KeyRound, History, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import { isoDay, todayIso, addDays } from '@/lib/localDate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

const DURATIONS = [
  { hours: 4, label: '4 hours' },
  { hours: 24, label: '1 day' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
];

const STATUS_STYLES = {
  pending: { icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { icon: ShieldCheck, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { icon: X, className: 'bg-rose-50 text-rose-700 border-rose-200' },
  revoked: { icon: Ban, className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.revoked;
  const Icon = style.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${style.className}`} data-testid={`identity-status-${status}`}>
      <Icon className="h-3 w-3" /> {status}
    </Badge>
  );
};

const expiryLabel = (grant) => {
  if (!grant.expires_at) return null;
  const left = new Date(grant.expires_at) - new Date();
  if (left <= 0) return 'expired';
  const hours = Math.floor(left / 3600000);
  return hours >= 24 ? `${Math.floor(hours / 24)}d left` : `${Math.max(hours, 1)}h left`;
};

const RequestRow = ({ grant, units, children }) => {
  const named = (grant.unit_ids || [])
    .map((id) => units[id] || id)
    .join(', ');
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300"
      data-testid={`identity-request-${grant.id || grant._id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusBadge status={grant.status} />
            {grant.detail_level === 'full_contact' && (
              <Badge variant="outline" className="text-[10px]">names + contact</Badge>
            )}
            {expiryLabel(grant) && (
              <span className="text-[11px] text-slate-500">{expiryLabel(grant)}</span>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-800">{grant.reason}</p>
          <p className="mt-1 text-xs text-slate-500">
            {named || 'no unit named'} · {grant.period_start} to {grant.period_end}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">{children}</div>
      </div>
    </div>
  );
};

export default function GivingIdentityRequestsPage() {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [unitId, setUnitId] = useState('');
  const [periodStart, setPeriodStart] = useState(isoDay(addDays(new Date(), -30)));
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [detail, setDetail] = useState('names_only');
  const [duration, setDuration] = useState('24');

  const mine = useQuery({
    queryKey: ['identity-requests', 'mine'],
    queryFn: () => call('/api/giving/identity-requests?mine=true'),
  });
  const queue = useQuery({
    queryKey: ['identity-requests', 'queue'],
    queryFn: () => call('/api/giving/identity-requests?mine=false&status=pending'),
  });
  const unitList = useQuery({
    queryKey: ['org-units', 'identity'],
    queryFn: () => call('/api/admin/org/units'),
  });

  const units = useMemo(() => {
    const rows = unitList.data?.units || unitList.data || [];
    return Object.fromEntries(
      (Array.isArray(rows) ? rows : []).map((u) => [u.id || u._id, `${u.name} (${u.level})`]),
    );
  }, [unitList.data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['identity-requests'] });

  const ask = useMutation({
    mutationFn: () => call('/api/giving/identity-requests', {
      method: 'POST',
      body: JSON.stringify({
        reason,
        unit_ids: [unitId],
        period_start: periodStart,
        period_end: periodEnd,
        detail_level: detail,
        duration_hours: Number(duration),
      }),
    }),
    onSuccess: () => {
      toast.success('Request filed — an approver other than you must grant it');
      setReason('');
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const decide = useMutation({
    mutationFn: ({ id, action, note }) => call(
      `/api/giving/identity-requests/${id}/${action}${note ? `?note=${encodeURIComponent(note)}` : ''}`,
      { method: 'POST' },
    ),
    onSuccess: (_d, v) => {
      toast.success(v.action === 'approve' ? 'Approved — it expires on its own'
        : v.action === 'revoke' ? 'Access withdrawn, effective immediately' : 'Refused');
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const myRequests = mine.data?.requests || [];
  const active = mine.data?.active || [];
  // The server already excludes your own requests from this list (iter 638);
  // the ids are filtered again here so a stale cache can never put an Approve
  // button on your own request.
  const myIds = new Set(myRequests.map((r) => r.id || r._id));
  const pending = (queue.data?.requests || [])
    .filter((r) => r.status === 'pending' && !myIds.has(r.id || r._id));
  const canSubmit = reason.trim().length >= 20 && unitId && periodStart && periodEnd;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="giving-identity-page">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <header className="mb-10 max-w-3xl">
          <a
            href="/admin/giving"
            className="inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-900"
            data-testid="identity-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Giving
          </a>
          <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-400">
            <EyeOff className="h-3.5 w-3.5" /> Giving privacy
          </div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            Who may see a giver&rsquo;s name
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Above assembly level, gifts are reported against a coded reference. A name is
            released only on an approved request — scoped to named units and a period,
            expiring on its own, and logged. You cannot approve your own.
          </p>
        </header>

        {active.length > 0 && (
          <Card className="mb-8 border-emerald-200 bg-emerald-50/40" data-testid="identity-active-grants">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <KeyRound className="h-4 w-4 text-emerald-600" /> Access you currently hold
              </CardTitle>
              <CardDescription>
                Re-checked on every single read, so withdrawing it takes effect at once.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {active.map((grant) => (
                <RequestRow key={grant.id || grant._id} grant={grant} units={units}>
                  <Button
                    size="sm" variant="outline"
                    data-testid="identity-revoke-button"
                    onClick={() => decide.mutate({ id: grant.id || grant._id, action: 'revoke' })}
                  >
                    <Ban className="mr-1 h-3.5 w-3.5" /> Hand it back
                  </Button>
                </RequestRow>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
          <Card data-testid="identity-request-form">
            <CardHeader>
              <CardTitle className="text-lg">Ask to see identities</CardTitle>
              <CardDescription>
                The reason is read by the approver, so write it for them.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identity-reason">Why do you need this?</Label>
                <Textarea
                  id="identity-reason" rows={4} value={reason}
                  data-testid="identity-reason-input"
                  placeholder="e.g. Reconciling the 2026 building fund against pledge cards at the trustees' request"
                  onChange={(e) => setReason(e.target.value)}
                />
                <p className={`text-xs ${reason.trim().length >= 20 ? 'text-slate-400' : 'text-amber-600'}`}>
                  {reason.trim().length}/20 characters minimum
                </p>
              </div>

              <div className="space-y-2">
                <Label>Which unit&rsquo;s giving?</Label>
                <Select value={unitId} onValueChange={setUnitId}>
                  <SelectTrigger data-testid="identity-unit-select">
                    <SelectValue placeholder="Choose a unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(units).map(([id, name]) => (
                      <SelectItem key={id} value={id}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="identity-from">Gifts from</Label>
                  <Input
                    id="identity-from" type="date" value={periodStart}
                    data-testid="identity-period-start"
                    onChange={(e) => setPeriodStart(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="identity-to">Gifts to</Label>
                  <Input
                    id="identity-to" type="date" value={periodEnd}
                    data-testid="identity-period-end"
                    onChange={(e) => setPeriodEnd(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>How much detail</Label>
                  <Select value={detail} onValueChange={setDetail}>
                    <SelectTrigger data-testid="identity-detail-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="names_only">Names only</SelectItem>
                      <SelectItem value="full_contact">Names and contact details</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>For how long</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger data-testid="identity-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map((d) => (
                        <SelectItem key={d.hours} value={String(d.hours)}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                className="w-full" disabled={!canSubmit || ask.isPending}
                data-testid="identity-submit-button"
                onClick={() => ask.mutate()}
              >
                {ask.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                File the request
              </Button>
            </CardContent>
          </Card>

          <div className="space-y-8">
            <Card data-testid="identity-approval-queue">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShieldCheck className="h-4 w-4 text-slate-400" /> Waiting for your decision
                </CardTitle>
                <CardDescription>
                  Requests from other people. Approving releases names for the scope stated,
                  and nothing wider.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {queue.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                {!queue.isLoading && pending.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="identity-queue-empty">
                    Nothing is waiting on you.
                  </p>
                )}
                {pending.map((grant) => (
                  <RequestRow key={grant.id || grant._id} grant={grant} units={units}>
                    <Button
                      size="sm" data-testid="identity-approve-button"
                      onClick={() => decide.mutate({ id: grant.id || grant._id, action: 'approve' })}
                    >
                      <Check className="mr-1 h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button
                      size="sm" variant="outline" data-testid="identity-reject-button"
                      onClick={() => decide.mutate({ id: grant.id || grant._id, action: 'reject' })}
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> Refuse
                    </Button>
                  </RequestRow>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="identity-my-requests">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <History className="h-4 w-4 text-slate-400" /> Everything you have asked for
                </CardTitle>
                <CardDescription>Kept whatever was decided — the log is the point.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {mine.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                {!mine.isLoading && myRequests.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="identity-mine-empty">
                    You have not asked to see any giver identities.
                  </p>
                )}
                {myRequests.map((grant) => (
                  <RequestRow key={grant.id || grant._id} grant={grant} units={units}>
                    {['approved', 'pending'].includes(grant.status) && (
                      <Button
                        size="sm" variant="outline" data-testid="identity-revoke-button"
                        onClick={() => decide.mutate({ id: grant.id || grant._id, action: 'revoke' })}
                      >
                        <Ban className="mr-1 h-3.5 w-3.5" />
                        {grant.status === 'pending' ? 'Withdraw' : 'Hand it back'}
                      </Button>
                    )}
                  </RequestRow>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
